import {type RequireSome} from '@wandb/weave/common/types/base';
import {
  type LayoutType,
  type MaskOptions,
  type MediaCardMetadata,
  type MediaCardType,
} from '@wandb/weave/common/types/media';
import {type GroupedList} from '@wandb/weave/common/util/data';
import {get, map, sum} from 'lodash';

import {metadataHasCaptions} from '../../util/media';
import {type PanelProps} from '../../util/panelProps';
import {type MediaBrowserPanelConfig} from './common';
import {type Dimensions, getGroupedMediaDimensions} from './dimensions';
import {LAYOUT_CONSTANTS} from './layout/constants';
import type {
  ControlData,
  GridData,
  RowData,
  SlotRegistry,
} from './layout/types';
import {fitToAvailableDimensions} from './sizing/fitToAvailableDimensions';
import {getFitSettingsForMediaType} from './sizing/getFitSettingsForMediaType';
import {type Tile} from './tiles/types';

export type MediaBrowserPanelProps = PanelProps<MediaBrowserPanelConfig>;
export interface RangeSettings {
  min: number;
  max: number;
  current: number;
  active?: boolean;
  label?: string;
}

export type SizingSettings = {
  columnCount: {
    min: number;
    max: number;
    current: number;
  };
};

export type Layout = {
  tiles: GroupedList<Tile>;
  galleryAxisData?: number[] | null;
  gridAxisData?: {
    x: number[];
    y: number[];
  } | null;
  // Maintain full scope x axis data so we can preserve zoom
  xAxisTicks?: number[];
  allXAxisValues?: number[];
  historySampleRate?: number;
  slots?: SlotRegistry;
  gridData?: GridData;
  controlData?: ControlData[];
  rowData?: RowData[];
};

export type MediaConfigWithDefaultsAndMediaKeys = RequireSome<
  MediaConfigWithDefaults,
  'mediaKeys'
>;

export const maskOptionsForLayout = (
  layoutType: LayoutType | null,
  maskKeys: string[]
): MaskOptions[] => {
  if (layoutType === null) {
    layoutType = 'MASKS_NEXT_TO_IMAGE';
  }
  if (layoutType === 'ALL_STACKED') {
    return [{maskKeys, showImage: true}];
  } else if (layoutType === 'ALL_SPLIT') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      ...map(maskKeys, k => ({
        maskKeys: [k],
        showImage: false,
      })),
    ];
  } else if (layoutType === 'MASKS_NEXT_TO_IMAGE') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      {
        maskKeys,
        showImage: false,
      },
    ];
  } else {
    // This case should never be hit it is used to hint the type system
    throw new Error('INVALID LAYOUT TYPE');
  }
};

export function calcGroupCount(config: MediaBrowserPanelConfig): number {
  return sum(
    map(config.mediaKeys, m => {
      const tileCount = get(config.tileLayout, [m, 'maskOptions', 'length'], 1);
      return tileCount;
    })
  );
}

type PanelContentSizing = {
  cardWidth: number;
  cardHeight: number;
  shrunk: boolean;
};

type GetPanelContentSizingParams = {
  config: Omit<MediaBrowserPanelConfig, 'chartTitle'>;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
  sizingSettings: SizingSettings;
  mediaType: MediaCardType;
  firstMediaMetadata: MediaCardMetadata | null;
};

export function getPanelContentSizing({
  config,
  mediaType,
  mediaBrowserSize,
  sizingSettings,
  firstMediaMetadata,
}: GetPanelContentSizingParams): PanelContentSizing {
  const {mode, compareSettings} = config;
  // Fit settings are scoped per media type, so panels of other media types
  // keep their layout when the image/video fit settings change (WB-36875).
  const {actualSize, fitToDimension} = getFitSettingsForMediaType(
    config,
    mediaType
  );
  const hasCaptions = metadataHasCaptions(firstMediaMetadata);
  const rowHeaderWidth =
    mode === 'grid' || (mode === 'compare' && compareSettings?.fanOutVariable)
      ? LAYOUT_CONSTANTS.ROW_HEADER_WIDTH
      : 0;
  const availableWidth =
    mediaBrowserSize.width -
    (sizingSettings.columnCount.current + 1) *
      LAYOUT_CONSTANTS.SPACE_BETWEEN_COLUMNS -
    rowHeaderWidth;
  const availableHeight =
    mediaBrowserSize.height -
    LAYOUT_CONSTANTS.TOP_AND_BOTTOM_PADDING -
    (hasCaptions ? LAYOUT_CONSTANTS.CAPTION_LINE_HEIGHT : 0);

  const groupedDimensions = getGroupedMediaDimensions(firstMediaMetadata);
  const mediaWidth = groupedDimensions?.width;
  const mediaHeight = groupedDimensions?.height;
  // In compare mode, don't let per-column width shrink below the grid's min column width;
  // gallery and grid have no such minimum and can keep shrinking.
  const rawCardWidth = availableWidth / sizingSettings.columnCount.current;
  const defaultCardWidth =
    mode === 'compare'
      ? Math.max(LAYOUT_CONSTANTS.MIN_COLUMN_WIDTH, rawCardWidth)
      : rawCardWidth;
  const defaultCardHeight = availableHeight;
  const {width: cardWidth, height: cardHeight} = getCardDimensions();

  // If the tiles don't fit in the media panel we want to shrink them
  // and change the layout so they're centered
  const heightOverflows = cardHeight > availableHeight;
  if (!actualSize && !fitToDimension && heightOverflows) {
    const aspectRatio = (mediaWidth ?? cardWidth) / (mediaHeight ?? cardHeight);
    return {
      cardWidth: availableHeight * aspectRatio,
      cardHeight: availableHeight,
      shrunk: true,
    };
  }

  return {
    cardWidth,
    cardHeight,
    shrunk: false,
  };

  function getCardDimensions(): Dimensions {
    if (mediaType === 'audio') {
      return {width: defaultCardWidth, height: 100};
    }
    if (mediaType === 'html' || mediaType === 'plotly') {
      return {width: defaultCardWidth, height: mediaBrowserSize.height};
    }
    if (mediaType === 'image' || mediaType === 'video') {
      return getImageDimensions();
    }
    return {width: defaultCardWidth, height: defaultCardWidth};
  }

  function getImageDimensions(): Dimensions {
    const intrinsicWidth = mediaWidth ?? defaultCardWidth;
    const intrinsicHeight = mediaHeight ?? defaultCardHeight;
    if (actualSize) {
      return {
        width: intrinsicWidth,
        height: intrinsicHeight,
      };
    }
    if (fitToDimension) {
      return fitToAvailableDimensions(
        [availableHeight, availableWidth],
        [intrinsicHeight, intrinsicWidth]
      );
    }
    // Size all cards by the column width and adjust the height
    // to maintain the aspect ratio, capped so the tile header fits.
    let width = defaultCardWidth;
    let height = (defaultCardWidth / intrinsicWidth) * intrinsicHeight;
    if (height > availableHeight) {
      height = availableHeight;
      width = (height / intrinsicHeight) * intrinsicWidth;
    }
    return {width, height};
  }
}

// A type for the config that contains defaults.
// Allows for cleaner type logic
export type MediaConfigWithDefaults = RequireSome<
  MediaBrowserPanelConfig,
  | 'actualSize'
  | 'fitToDimension'
  | 'videoActualSize'
  | 'columnCount'
  | 'mode'
  | 'pixelated'
  | 'gallerySettings'
  | 'gridSettings'
  | 'maxYAxisCount'
  | 'snapToExistingStep'
  | 'alwaysOriginal'
>;
